import React from 'react';

import { useCallback, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';

type TagInputProps = {
  placeholder?: string;
  tags: string[];
  setTags: (tags: string[]) => void;
  className?: string;
  inputClassName?: string;
  tagClassName?: string;
  disabled?: boolean;
};

export const TagInput = ({
  placeholder = 'Add a tag...',
  tags,
  setTags,
  className,
  inputClassName,
  tagClassName,
  disabled = false
}: TagInputProps) => {
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (disabled) return;
    if ((e.key === 'Enter' || e.key === ',', e.key === ' ')) {
      e.preventDefault();
      addTag();
    } else if (e.key === 'Backspace' && inputValue === '' && tags.length > 0) {
      e.preventDefault();
      setTags(tags.slice(0, -1));
    }
  };

  const addTag = useCallback(() => {
    if (disabled) return;
    const trimmedValue = inputValue.trim();
    if (trimmedValue && !tags.includes(trimmedValue)) {
      setTags([...tags, trimmedValue]);
      setInputValue('');
    }
  }, [inputValue, tags, setTags, disabled]);

  const removeTag = (tagToRemove: string) => {
    if (disabled) return;
    setTags(tags.filter((tag) => tag !== tagToRemove));
  };

  const handleBlur = () => {
    if (inputValue.trim()) {
      addTag();
    }
  };

 /*  // Focus the input when the component mounts
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []); */

  return (
    <div
      className={cn(
        'flex flex-wrap items-center gap-2 rounded-md border border-input bg-gray-50 px-3 py-2',
        disabled && 'opacity-50 cursor-not-allowed',
        className
      )}
      onClick={() => !disabled && inputRef.current?.focus()}
    >
      {tags.map((tag) => (
        <div
          key={tag}
          className={cn(
            'flex items-center gap-1 rounded-full bg-primary px-3 py-1 text-sm text-primary-foreground text-white',
            tagClassName
          )}
        >
          <span className=''>{tag}</span>
          {!disabled && (
            <button
              type='button'
              onClick={(e) => {
                e.stopPropagation();
                removeTag(tag);
              }}
              className='rounded-full hover:bg-primary/80'
            >
              <X className='h-3 w-3' />
            </button>
          )}
        </div>
      ))}
      <input
        ref={inputRef}
        type='text'
        value={inputValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        placeholder={tags.length === 0 ? placeholder : ''}
        disabled={disabled}
        className={cn(
          'placeholder:text-grey-50 flex-1 bg-transparent text-sm outline-none',
          disabled && 'cursor-not-allowed',
          inputClassName
        )}
      />
    </div>
  );
};
